import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../../generic/Api_Functions'
import useMosInfo from '../common/provider/useMosInfo'
import CenteredText from '../common/CenteredText'
import DefaultBeforeSelectText from '../common/DefaultBeforeSelectText'

// Sample rows shown in the widget picker preview (no device/job selected there).
const PREVIEW_PARTS = [
  { id: 1, code: 'P-001', name: 'Sample part A' },
  { id: 2, code: 'P-002', name: 'Sample part B' }
]

// Lists the parts of the current active job that can still be reported, from
//   GET mos/fabricationOrderReportQuantity/reportableParts/{fabricationOrderId}/{jobReferenceId}
// which returns List<IdCodeNameDto> => [{ id, code, name }]. The fabrication order and job
// reference come from the shared MOS context (the active job selected on this device).
const ReportablePartsWidget = ({ preview = false }) => {
  const { t_ } = useSmartTranslation()
  const { fabricationOrder, jobReference } = useMosInfo()
  const [parts, setParts] = useState([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const fabricationOrderId = fabricationOrder?.id
  const jobReferenceId = jobReference?.id

  useEffect(() => {
    if (preview) return
    if (!fabricationOrderId || !jobReferenceId) {
      setParts([])
      setLoaded(false)
      return
    }
    let cancelled = false
    const loadParts = async () => {
      setLoading(true)
      try {
        const result = await apiGet('mos/fabricationOrderReportQuantity/reportableParts/' + fabricationOrderId + '/' + jobReferenceId)
        if (!cancelled) setParts(Array.isArray(result) ? result : [])
      } catch (error) {
        if (!cancelled) setParts([])
      } finally {
        if (!cancelled) {
          setLoading(false)
          setLoaded(true)
        }
      }
    }
    loadParts()
    return () => { cancelled = true }
  }, [preview, fabricationOrderId, jobReferenceId])

  const rows = useMemo(() => (preview ? PREVIEW_PARTS : parts), [preview, parts])

  if (!preview && (!fabricationOrderId || !jobReferenceId)) {
    return <DefaultBeforeSelectText />
  }

  if (loading) {
    return <CenteredText><CircularProgress /></CenteredText>
  }

  if (!preview && loaded && rows.length === 0) {
    return (
      <CenteredText>
        <Typography variant='body2' color='text.secondary'>{t_('No reportable parts')}</Typography>
      </CenteredText>
    )
  }

  return (
    <Box sx={{ height: '100%', width: '100%', overflow: 'auto' }}>
      <Table size='small' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{t_('Id')}</TableCell>
            <TableCell>{t_('Code')}</TableCell>
            <TableCell>{t_('Name')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((part, index) => (
            <TableRow key={part.id ?? index}>
              <TableCell>{part.id}</TableCell>
              <TableCell>{part.code}</TableCell>
              <TableCell>{part.name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default ReportablePartsWidget
